/**
 * Facade for the Projects API.
 * Backed by the real backend (bimiq-backend-python). No mock layer.
 */

import { ApiFacade } from './apiFacade';
import { Project } from '../../@types/projects';
import { API_BASE_URL } from '../../apiConfig';

const PROJECTS_URL = `${API_BASE_URL}/api/v1/projects`;
const MY_PROJECTS_URL = `${API_BASE_URL}/api/v1/users/me/projects`;

function projectUrl(projectId: string): string {
    return `${PROJECTS_URL}/${encodeURIComponent(projectId)}`;
}

async function getProjectList(accessToken: string, url: string): Promise<Project[]> {
    const response = await ApiFacade.getJson<{ projects: Project[] }>(url, { accessToken });
    if (!Array.isArray(response?.projects)) {
        throw new Error('Invalid project list response.');
    }
    return response.projects;
}

/**
 * Every project the caller's organization owns, the administrative view,
 * org_admin only. What the caller is a *member* of is a different question,
 * answered by `getMyProjects`.
 */
export async function getProjects(accessToken: string): Promise<Project[]> {
    return getProjectList(accessToken, PROJECTS_URL);
}

/**
 * Projects the caller holds a membership row for, across organizations. Does
 * not include projects they merely belong to the owning org of, not even for
 * an org admin, whose implicit access to every org project is not a membership.
 */
export async function getMyProjects(accessToken: string): Promise<Project[]> {
    return getProjectList(accessToken, MY_PROJECTS_URL);
}

export async function getProject(accessToken: string, projectId: string): Promise<Project> {
    return ApiFacade.getJson<Project>(projectUrl(projectId), { accessToken });
}

export async function createProject(accessToken: string, name: string): Promise<Project> {
    return ApiFacade.postJson<Project>(PROJECTS_URL, { accessToken, body: { name } });
}

export async function renameProject(accessToken: string, projectId: string, name: string): Promise<Project> {
    return ApiFacade.patchJson<Project>(projectUrl(projectId), { accessToken, body: { name } });
}

/**
 * Cascades memberships, invitations, rule *assignments* and the APS connection.
 * The rules themselves are org-owned and survive untouched.
 */
export async function deleteProject(accessToken: string, projectId: string): Promise<void> {
    await ApiFacade.deleteJson(projectUrl(projectId), { accessToken });
}
